/**
 * useInViewport.ts
 *
 * Lightweight IntersectionObserver wrapper that reports whether the referenced
 * element is in (or near) the scroll viewport. Used to defer expensive queries
 * for below-the-fold sections until the user actually scrolls them into view.
 */
import { useEffect, useRef, useState } from 'react';

export interface UseInViewportOptions {
  /** When true (default), latches `inView` to true on first intersection and disconnects. */
  once?: boolean;
  /** IntersectionObserver root margin. Default '200px' to start fetches just before the section appears. */
  rootMargin?: string;
  /** IntersectionObserver threshold. */
  threshold?: number | number[];
  /** Skip the observer entirely and treat as in-view. Useful while patientId is null. */
  skip?: boolean;
}

export function useInViewport<T extends Element = Element>(
  options: UseInViewportOptions = {},
  // RefObject<T>, not RefObject<T | null>. `RefObject<T>` already declares
  // `readonly current: T | null`, so the union added nothing — and it is
  // covariant in T, which means RefObject<T | null> is NOT assignable to the
  // RefObject<T> that a JSX `ref` prop expects. Every consumer that attached
  // this to a <div> was a TS2322, five of them in PatientDashboardSections
  // alone, all saying 'null is not assignable to HTMLDivElement'.
): { ref: React.RefObject<T>; inView: boolean } {
  const { once = true, rootMargin = '200px', threshold = 0, skip = false } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (skip) {
      setInView(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, skip]);

  return { ref, inView };
}
